import React, { useEffect, useRef, useState } from 'react';

export interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  placement = 'top',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      let newPosition = { top: 0, left: 0 };
      
      switch (placement) {
        case 'top':
          newPosition.top = triggerRect.top - tooltipRect.height - 8;
          newPosition.left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
          newPosition.top = triggerRect.bottom + 8;
          newPosition.left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'left':
          newPosition.top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          newPosition.left = triggerRect.left - tooltipRect.width - 8;
          break;
        case 'right':
          newPosition.top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          newPosition.left = triggerRect.right + 8;
          break;
      }
      
      setPosition(newPosition);
    }
  }, [isVisible, placement]);

  const placementClasses = {
    top: 'mb-2',
    bottom: 'mt-2',
    left: 'mr-2',
    right: 'ml-2'
  };

  return (
    <span
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative inline-block"
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 bg-[var(--color-dark)] text-white text-sm px-2 py-1 rounded shadow-lg ${placementClasses[placement]} ${className || ''}`}
          style={{ top: `${position.top}px`, left: `${position.left}px`, position: 'absolute' }}
          role="tooltip"
        >
          {content}
        </div>
      )}
    </span>
  );
};

export interface UncontrolledTooltipProps {
  target: string;
  children: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export const UncontrolledTooltip: React.FC<UncontrolledTooltipProps> = ({
  target,
  children,
  placement = 'top',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const triggerElement = document.getElementById(target);
    if (!triggerElement) return;

    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => setIsVisible(false);

    triggerElement.addEventListener('mouseenter', handleMouseEnter);
    triggerElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      triggerElement.removeEventListener('mouseenter', handleMouseEnter);
      triggerElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [target]);

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      const triggerElement = document.getElementById(target);
      if (!triggerElement) return;

      const triggerRect = triggerElement.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      let newPosition = { top: 0, left: 0 };
      
      switch (placement) {
        case 'top':
          newPosition.top = triggerRect.top - tooltipRect.height - 8;
          newPosition.left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
          newPosition.top = triggerRect.bottom + 8;
          newPosition.left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'left':
          newPosition.top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          newPosition.left = triggerRect.left - tooltipRect.width - 8;
          break;
        case 'right':
          newPosition.top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          newPosition.left = triggerRect.right + 8;
          break;
      }
      
      setPosition(newPosition);
    }
  }, [isVisible, placement, target]);

  const placementClasses = {
    top: 'mb-2',
    bottom: 'mt-2',
    left: 'mr-2',
    right: 'ml-2'
  };

  if (!isVisible) return null;

  return (
    <div
      ref={tooltipRef}
      className={`fixed z-50 bg-[var(--color-dark)] text-white text-sm px-2 py-1 rounded shadow-lg ${placementClasses[placement]} ${className || ''}`}
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
      role="tooltip"
    >
      {children}
    </div>
  );
};
